import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { RestfulEndpoint } from '../../../services/restful-endpoint.service';
import { ToastrService } from 'ngx-toastr';
import { TranslocoService } from '@ngneat/transloco';
import {
  ClientUser,
  FetchUserSearchParameters,
  SharedProjectsUsersResponse,
  UsersRefactoredResponse,
  UsersStatisticsResponse,
  UserStatisticsData,
} from '../../../models/users.model';
import { environment } from '../../../../environments/environment';
import { Title } from '@angular/platform-browser';
import { tap } from 'rxjs/operators';
import { CountryISO, PhoneNumberFormat, SearchCountryField } from 'ngx-intl-tel-input';

@Injectable({
  providedIn: 'root',
})
export class UserManagementService implements OnDestroy {
  private subscriptions: Subscription = new Subscription();

  constructor(
    private endPoint: RestfulEndpoint,
    private _toaster: ToastrService,
    private translocoService: TranslocoService,
    private titleService: Title,
  ) {
    // Title is managed by admin-layout component based on route data
  }

  //#region Private Variables
  private _searchParams: FetchUserSearchParameters = {
    page: 1,
    per_page: 10,
  };

  private _totalUsersSubject = new BehaviorSubject<number>(0);
  private _usersSubject = new BehaviorSubject<ClientUser[]>([]);
  private _usersSummarySubject = new BehaviorSubject<UserStatisticsData>({} as UserStatisticsData);
  private _searchParamsSubject = new BehaviorSubject<FetchUserSearchParameters>(this._searchParams);

  private _totalUsers: number = 0;
  private _users: ClientUser[] = [];
  private _usersSummary: UserStatisticsData = {} as UserStatisticsData;
  //#endregion

  //#region Setter Functions
  setSearchParams(params: Partial<FetchUserSearchParameters>) {
    this._searchParams = { ...this._searchParams, ...params };
    this._searchParamsSubject.next(this._searchParams);
  }

  resetSearchParams() {
    this._searchParams = {
      role: undefined,
      page: 1,
      per_page: 10,
      email: undefined,
      status: undefined,
      username: undefined,
    };
    this._searchParamsSubject.next(this._searchParams);
  }

  //#endregion

  //#region Getter Functions
  get totalUsers(): number {
    return this._totalUsersSubject.value;
  }

  get totalLicenses(): number {
    return this.usersSummary.balance_of_users + this.usersSummary.total_users;
  }

  get searchParametrts(): FetchUserSearchParameters {
    return this._searchParamsSubject.value;
  }

  get users(): ClientUser[] {
    return this._usersSubject.value;
  }

  set users(value) {
    this._usersSubject.next(value);
  }

  get usersSummary(): UserStatisticsData {
    return this._usersSummarySubject.value;
  }

  //#endregion

  //#region CRUD Operations
  changeStatus(id: string, action_type: 'active' | 'deactivate'): Observable<boolean> {
    return this.endPoint.post('change_status', { id, action_type }).pipe(
      tap({
        next: (response) => {
          if (response) {
            this.subscriptions.add(this.fetchUsersList().subscribe());
            this.subscriptions.add(this.fetchUsersSummary().subscribe());
            if (action_type === 'active') {
              this._toaster.success(
                this.translocoService.translate('User Status is successfully Activated.'),
              );
            } else {
              this._toaster.warning(
                this.translocoService.translate('User Status is successfully Deactivated.'),
              );
            }
            return true;
          } else {
            return false;
          }
        },
        error: (error) => {
          this.errorHandling(error);
        },
      }),
    );
  }

  createUser(value: ClientUser): Observable<ClientUser> {
    return this.endPoint.post('', value, null, 'users').pipe(
      tap({
        next: (response) => {
          if (response) {
            this._toaster.success(
              this.translocoService.translate(
                value.user_type == 'ORG_ADMIN'
                  ? 'User successfully added as Admin.'
                  : 'User is Successfully created',
              ),
            );
            value.user_type == 'ORG_ADMIN' && value.extra_data.is_viewer
              ? this._toaster.success(
                  this.translocoService.translate(
                    'The user has been added as a viewer to all current projects.',
                  ),
                )
              : null;
            this.subscriptions.add(this.fetchUsersSummary().subscribe());
            this.subscriptions.add(this.fetchUsersList().subscribe());
          }
        },
        error: (error) => {
          this.errorHandling(error);
        },
      }),
    );
  }

  updateUser(value: any): Observable<ClientUser> {
    return this.endPoint.post(value.id, value, null, 'users').pipe(
      tap({
        next: (response) => {
          if (response) {
            this._toaster.success(this.translocoService.translate('User is Successfully updated'));
          }
        },
        error: (error) => {
          this.errorHandling(error);
        },
      }),
    );
  }

  //#endregion

  //#region Fetch Methods
  fetchUsersList(): Observable<UsersRefactoredResponse> {
    this._totalUsersSubject.next(null);
    return this.endPoint.get(`users/`, this._searchParams).pipe(
      tap({
        next: (response: UsersRefactoredResponse) => {
          this._totalUsers = response.meta_data?.total_items;
          this._totalUsersSubject.next(this._totalUsers);
          this._users = response.data;
          this._usersSubject.next(this._users);
        },
        error: (error) => {
          this.handleErrorsV2(error);
        },
      }),
    );
  }

  fetchUsersSummary(): Observable<UsersStatisticsResponse> {
    return this.endPoint.get(`users/summary`).pipe(
      tap({
        next: (response: UsersStatisticsResponse) => {
          this._usersSummary = response.data;
          this._usersSummarySubject.next(this._usersSummary);
        },
        error: (error) => {
          this.handleErrorsV2(error);
        },
      }),
    );
  }

  fetchSharedProjects(
    userId: string,
    perPage: number = 20,
    page: number = 1,
  ): Observable<SharedProjectsUsersResponse> {
    return this.endPoint
      .get(`users/${userId}/projects`, {
        per_page: perPage,
        page: page,
        include_sub_projects: 1,
      })
      .pipe(
        tap({
          next: (response: SharedProjectsUsersResponse) => {},
          error: (error) => {
            this.handleErrorsV2(error);
          },
        }),
      );
  }

  //#endregion

  //#region Utility Methods
  generatePassword(): string {
    // Define character sets for each requirement
    const lowercase = 'abcdefghijklmnopqrstuvwxyz';
    const uppercase = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    const digits = '0123456789';
    const specialChars = '@#$%&*';

    // Ensure at least one character from each required category
    let password = '';
    password += lowercase[Math.floor(Math.random() * lowercase.length)];
    password += uppercase[Math.floor(Math.random() * uppercase.length)];
    password += digits[Math.floor(Math.random() * digits.length)];
    password += specialChars[Math.floor(Math.random() * specialChars.length)];

    // Fill remaining characters (total length: 12) with random mix
    const allChars = lowercase + uppercase + digits + specialChars;
    for (let i = 4; i < 12; i++) {
      password += allChars[Math.floor(Math.random() * allChars.length)];
    }

    // Shuffle the password to avoid predictable patterns
    return password
      .split('')
      .sort(() => Math.random() - 0.5)
      .join('');
  }

  errorHandling(reason: any): void {
    if (reason.error && reason.error.error) {
      this._toaster.error(this.translocoService.translate(reason.error.error.join(',')));
    } else if (reason.error && reason.error.errors) {
      //handle v2 error till global error handler is applied
      reason.error.errors.forEach((error) => {
        this._toaster.error(error);
      });
    } else {
      this._toaster.error(
        this.translocoService.translate('Something went wrong, please try again later.'),
      );
    }
  }

  handleErrorsV2(error: any) {
    this._toaster.error(error.error.message);
  }

  getIntlNumOptions() {
    // Get current language from localStorage or default to 'en'
    // Use localStorage as fallback since TranslocoService might not be available in service
    const currentLang = localStorage.getItem('lang') || 'en';
    // Set default country to Russia only if language is Russian, otherwise Saudi Arabia
    const defaultCountry = currentLang === 'ru' ? CountryISO.Russia : CountryISO.SaudiArabia;

    return {
      countryISO: CountryISO,
      defaultCountry: defaultCountry,
      searchCountryField: SearchCountryField,
      phoneNumberFormat: PhoneNumberFormat,
      countriesNeeded: [
        CountryISO.UnitedKingdom,
        CountryISO.Jordan,
        CountryISO.Bahrain,
        CountryISO.Germany,
        CountryISO.Iraq,
        CountryISO.Kazakhstan,
        CountryISO.Mexico,
        CountryISO.UnitedStates,
        CountryISO.Kuwait,
        CountryISO.Oman,
        CountryISO.UnitedArabEmirates,
        CountryISO.Qatar,
        CountryISO.SaudiArabia,
        CountryISO.India,
        CountryISO.Italy,
        CountryISO.Egypt,
        CountryISO.Romania,
        CountryISO.Russia,
        CountryISO.Turkey,
      ],
    };
  }

  changeIntlNumCountry($event, form) {
    if (typeof form.value.phone_number == 'string') {
      return;
    }
    let currentValue = '';
    if (
      form.value.phone_number &&
      form.value.phone_number.number.includes(form.value.phone_number.dialCode)
    ) {
      currentValue = form.value.phone_number
        ? form.value.phone_number.number.replace(
            form.value.phone_number.dialCode,
            '+' + $event.dialCode,
          )
        : '+' + $event.dialCode;
    } else {
      currentValue = '+' + $event.dialCode + (form.value.phone_number?.number ?? '');
    }

    form.get('phone_number').setValue(currentValue);
  }

  fetchUserRoles() {
    return this.endPoint.get('users/roles');
  }

  //#endregion

  //#region Lifecycle Hooks
  ngOnDestroy() {
    this.subscriptions.unsubscribe();
    this._usersSubject.next([]);
    this._totalUsersSubject.next(0);
    this._searchParamsSubject.next({
      page: 1,
      per_page: 10,
    });
  }

  //#endregion
}
